import React, { useCallback, useMemo } from 'react';
import { TSession, determineState, State } from './Sessions';
import { withRouter, RouteComponentProps } from 'react-router';
import styled from 'styled-components';
import { Input, Error, CheckCircle } from '@material-ui/icons';
import { Tooltip } from '@material-ui/core';
import { Privilege, RunState } from '../../api';
import { steps } from './SessionPrepareFlow';
import useHasPrivilege from '../Util/useHasPrivilege';

interface Props extends RouteComponentProps {
  session: TSession;
}

const SessionPrepareButton: React.FunctionComponent<Props> = ({ session, history }) => {
  const state = determineState(session);
  const isAdmin = useHasPrivilege(Privilege.Admin);
  const isRunning = useMemo(
    () =>
      session.lastRun?.state !== RunState.Created &&
      session.lastRun?.state !== RunState.Stopped &&
      session.lastRun?.state !== RunState.Deleted &&
      !!session.lastRun,
    [session]
  );

  const onClick = useCallback(
    (event) => {
      if (!isRunning) {
        event.preventDefault();
        event.stopPropagation();
        history.push('/session/prepare/' + session.id);
      }
    },
    [session, history, isRunning]
  );

  const isComplete = useMemo(() => {
    if (!session) return false;
    const prepareCompleted = session.states?.prepare?.isCompleted;
    if (prepareCompleted !== undefined && prepareCompleted !== null) return prepareCompleted;
    for (let element of steps(isAdmin)) {
      if (!element.filter || element.filter(session)) {
        const screenComplete = element.component.isComplete(session);
        if (!screenComplete) {
          return false;
        }
      }
    }
    return true;
  }, [session, isAdmin]);

  if (state === State.Prepare || state === State.Run || state === State.Label) {
    let child;
    if (isComplete) {
      if (!isRunning) {
        child = (
          <Tooltip title="All session information has been entered, session can be started">
            <CheckCircle color="secondary" />
          </Tooltip>
        );
      } else {
        child = (
          <Tooltip title="All session information has been entered">
            <CheckCircle color="secondary" />
          </Tooltip>
        );
      }
    } else {
      child = (
        <Tooltip title="Start session preparation">
          <Input color="secondary" />
        </Tooltip>
      );
    }
    return (
      <LinkDiv onClick={onClick} disabled={isRunning}>
        {child}
      </LinkDiv>
    );
  }
  return <div />;
};

type LinkDivProps = {
  disabled?: boolean;
};
export const LinkDiv = styled.div<LinkDivProps>`
  cursor: ${(props) => (props.disabled ? 'normal' : 'pointer')};
  .sublabel {
    font-size: 12px;
  }
`;

export const Link = styled.div`
  text-decoration: underline;
  color: #4141f7;
  cursor: pointer;
  white-space: initial;
`;

export const StyledError = styled(Error)`
  color: red;
`;
export default withRouter(SessionPrepareButton);
